/**
 * XRCapabilities2 — Runtime capability matrix for graphics + XR backends.
 *
 * Detects what the current browser supports without making speculative
 * WebXR session requests (those are gesture-gated).
 */

export type GfxBackend = 'webgpu' | 'webgl2';
export type XrBinding = 'webgl' | 'webgpu';

export interface XrCaps {
  api: 'webxr';
  binding: XrBinding;
  supportsLayers: boolean;
  supportsMultiview: boolean;
  depthRange: 'neg1to1' | '0to1';
  layerTypes: Set<'projection' | 'quad' | 'cylinder' | 'equirect' | 'cube'>;
}

export interface RuntimeCaps {
  gfx: GfxBackend;
  xr: false | XrCaps;
  postFXAllowed: boolean;
}

/**
 * Detect base capabilities from globals (synchronous, no XR session needed).
 */
export function detectBaseCapabilities(): { hasWebXR: boolean; hasWebGPU: boolean } {
  return {
    hasWebXR: typeof navigator !== 'undefined' && 'xr' in navigator,
    hasWebGPU: typeof navigator !== 'undefined' && 'gpu' in navigator,
  };
}

/**
 * Create a default RuntimeCaps with no XR active.
 */
export function createDefaultRuntimeCaps(gfx: GfxBackend): RuntimeCaps {
  return {
    gfx,
    xr: false,
    postFXAllowed: true,
  };
}

/**
 * Create XrCaps from known binding type and optional overrides.
 */
export function createXrCaps(
  binding: XrBinding,
  opts: {
    supportsLayers?: boolean;
    supportsMultiview?: boolean;
    layerTypes?: Set<'projection' | 'quad' | 'cylinder' | 'equirect' | 'cube'>;
  } = {},
): XrCaps {
  return {
    api: 'webxr',
    binding,
    supportsLayers: opts.supportsLayers ?? false,
    supportsMultiview: opts.supportsMultiview ?? false,
    depthRange: binding === 'webgpu' ? '0to1' : 'neg1to1',
    layerTypes: opts.layerTypes ?? new Set(['projection']),
  };
}
